import { useEffect, useState } from 'react'
import Button from '../common/Button'
import Modal from '../common/Modal'

import { useCreateOrderForCustomerMutation } from '@/services/customers/customers'
import { useLazyGetAllTeamsQuery } from '@/services/tenant/teamManagement'
import { useLazyGetServicesListQuery } from '@/services/tenant/webPage'
import { forwardGeocode } from '@/utils/helpers'
import { yupResolver } from '@hookform/resolvers/yup'
import { Country, ICountry, IState, State } from 'country-state-city'
import { capitalize } from 'lodash'
import { Controller, FieldErrors, useForm, useWatch } from 'react-hook-form'
import { toast } from 'react-toastify'
import * as yup from 'yup'
import AppDatePicker from '../common/AppDatePicker'
import Input, { InputStyles } from '../common/Input'
import SelectBox from '../common/SelectBox'
import SelectBoxAsynk, {
    SelectAsyncetchFunction,
} from '../common/SelectBoxAsynk'
import TextArea from '../common/TextArea'
import TimeRangePicker from '../common/TimeRangePicker'

interface Props {
    isOpen: boolean
    onClose: () => void
    customerId: number
}

const statuses = [
    'pending',
    'confirmed',
    'inProgress',
    'arrived',
    'start_job',
    'completed',
    'canceled',
]
const statusOptions = statuses.map(status => ({
    value: status,
    label: capitalize(status),
}))

const dateSchema = yup.object({
    date: yup.date().required('Date is required').typeError('Date is required'),
    timeRange: yup
        .string()
        .required('Time range is required')
        .typeError('Time range is required')
        .matches(
            /^\d{2}:\d{2} - \d{2}:\d{2}$/,
            'Time range format must be HH:MM - HH:MM',
        )
        .test(
            'start-before-end',
            'Start time must be before end time',
            timeRange => {
                const [start, end] = timeRange.split('-').map(s => s.trim())
                const startTime = new Date(`1970-01-01T${start}:00`)
                const endTime = new Date(`1970-01-01T${end}:00`)
                return startTime < endTime
            },
        ),
})

const orderFormSchema = yup
    .object({
        note: yup.string().required('Note is required'),
        address: yup
            .object({
                street: yup.string().required('Street is required'),
                city: yup.string().required('City is required'),
                state: yup.string().required('State is required'),
                postalCode: yup.string().required('Postal code is required'),
                country: yup.string().required('Country is required'),
                latitude: yup
                    .number()
                    .typeError('Latitude is required')
                    .optional(),
                longitude: yup
                    .number()
                    .typeError('Longitude is required')
                    .optional(),
            })
            .required('Address is required'),
        status: yup
            .string()
            .oneOf(statuses)
            .typeError('Status is required')
            .required('Status is required'),
        assignments: yup
            .object({
                type: yup
                    .string()
                    .oneOf(['user', 'team', 'group'])
                    .typeError('Assignment type is required')
                    .required('Assignment type is required'),
                refId: yup
                    .number()
                    .typeError('Assignment technician team is required')
                    .required('Assignment technician team is required'),
            })
            .required('Assignments is required'),
    })
    .concat(dateSchema)

const orderRequestSchema = orderFormSchema.transform(value => {
    if (!value) return value

    const { date, timeRange, ...rest } = value

    if (!date || !timeRange) return value

    const [startStr, endStr] = timeRange.split('-').map((s: string) => s.trim())

    // parse times
    const [startH, startM] = startStr.split(':').map(Number)
    const [endH, endM] = endStr.split(':').map(Number)

    const startTime = new Date(date)
    startTime.setHours(startH, startM, 0, 0)

    const endTime = new Date(date)
    endTime.setHours(endH, endM, 0, 0)

    return {
        ...value,
        startTime: startTime.toISOString(),
        endTime: endTime.toISOString(),
    }
})

export type OrderFormValues = Omit<
    yup.InferType<typeof orderRequestSchema>,
    'date' | 'timeRange'
>

const AddOrderModal = ({ isOpen, onClose, customerId }: Props) => {
    const [createOrderForCustomer] = useCreateOrderForCustomerMutation()
    const [selectedServiceOption, setSelectedServiceOption] = useState<{
        value: number
        label: string
    } | null>(null)
    const [selectedTeamOption, setSelectedTeamOption] = useState(null)
    const [states, setStates] = useState<{ value: string; label: string }[]>([])

    const [getServices] = useLazyGetServicesListQuery({})
    const [getAllTeams] = useLazyGetAllTeamsQuery()

    const {
        control,
        register,
        handleSubmit,
        formState: { errors, isSubmitting },
    } = useForm({
        resolver: yupResolver(orderFormSchema),
        defaultValues: {
            assignments: {
                type: 'team',
            },
        },
    })

    const onSubmit = async (data: OrderFormValues) => {
        const address = data.address
        const fullAddress = `${address?.street}, ${address?.city}, ${address?.state}`

        const coordinates = await forwardGeocode(fullAddress)
        data.address = {
            ...address,
            latitude: coordinates?.lat,
            longitude: coordinates?.lng,
        }
        const { date, timeRange, ...body } = orderRequestSchema.cast(data)
        if (!selectedServiceOption?.value) {
            toast.error('Service is required')
            return
        }
        const response = await createOrderForCustomer({
            serviceId: selectedServiceOption.value,
            customerId,
            body,
        }).unwrap()
        if (response?.success) {
            toast.success('Order created successfully!')
            onClose()
        } else {
            toast.error(response?.message || 'Something went wrong')
        }
    }
    const onError = (errots: FieldErrors<OrderFormValues>) =>
        console.log(errots)

    const loadServices: SelectAsyncetchFunction = async (
        search,
        loadedOptions,
        { page },
    ) => {
        try {
            const response = await getServices(
                `page=${page}&search=${search}`,
            ).unwrap()
            if (!response?.success)
                throw new Error(response.message || 'Failed to load services')

            return {
                options:
                    response?.data?.map((item: any) => ({
                        value: item.id,
                        label: item.name,
                    })) || [],
                hasMore: +page !== response?.pagination?.maxPages,
                additional: { page: page + 1 },
            }
        } catch (error) {
            console.log(error)
            toast.error('Failed to load services')
            return {
                options: [],
                hasMore: false,
            }
        }
    }
    const loadTeams: SelectAsyncetchFunction = async (
        search,
        loadedOptions,
        { page },
    ) => {
        try {
            const response = await getAllTeams(
                `page=${page}&search=${search}`,
            ).unwrap()
            if (!response?.success)
                throw new Error(response.message || 'Failed to load teams')
            return {
                options:
                    response?.data?.map(item => ({
                        value: item.team.id,
                        label: item.team.name,
                    })) || [],
                hasMore: +page !== response?.pagination?.maxPages,
                additional: { page: page + 1 },
            }
        } catch (error) {
            console.log(error)
            toast.error('Failed to load teams')
            return {
                options: [],
                hasMore: false,
            }
        }
    }

    const country = useWatch({ control, name: 'address.country' })

    const allCountries = Country.getAllCountries()
    const countries =
        allCountries?.map((item: ICountry) => ({
            value: item.isoCode,
            label: item.name,
        })) || []

    const getStates = (country: string) => {
        if (country) {
            const allStates = State.getStatesOfCountry(country)
            setStates(
                allStates?.map((item: IState) => ({
                    value: item.isoCode,
                    label: item.name,
                })) || [],
            )
        } else {
            setStates([])
        }
    }

    useEffect(() => {
        const countryTemp =
            countries?.find(
                one => one?.label === country || one?.value === country,
            )?.value || ''
        getStates(countryTemp)
    }, [country])

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title="Add Order"
        >
            <div className="modal__body p-0">
                <form
                    className="custom-form"
                    onSubmit={handleSubmit(onSubmit, onError)}
                >
                    <div
                        className="custom-form-body mb-42 overflow-y-auto"
                        style={{ maxHeight: 'calc(100vh - 300px)' }}
                    >
                        <div className="row gutters-16">
                            <div className="col-12">
                                <SelectBoxAsynk
                                    wrapperClassName="input--lg select-secondary select-left-spase-0"
                                    label="Select Service"
                                    placeholder="Select Service"
                                    fetchFunk={loadServices}
                                    onChange={setSelectedServiceOption}
                                    value={selectedServiceOption}
                                />
                            </div>
                            <div className="col-12">
                                <Controller
                                    name="address.country"
                                    control={control}
                                    render={({ field }) => (
                                        <SelectBox
                                            {...field}
                                            wrapperClassName="input--lg select-secondary select-left-spase-0"
                                            label="Country"
                                            options={countries}
                                            placeholder={'Select Country'}
                                            error={
                                                errors.address?.country?.message
                                            }
                                            onChange={option =>
                                                field.onChange(option?.value)
                                            }
                                            value={
                                                countries.find(
                                                    option =>
                                                        option.label ===
                                                            field.value ||
                                                        option.value ===
                                                            field.value,
                                                )?.value
                                            }
                                        />
                                    )}
                                />
                            </div>
                            <div className="col-12">
                                <Controller
                                    name="address.state"
                                    control={control}
                                    render={({ field }) => (
                                        <SelectBox
                                            {...field}
                                            wrapperClassName="input--lg select-secondary select-left-spase-0"
                                            label="State"
                                            options={states}
                                            placeholder="Select State"
                                            error={
                                                errors.address?.state?.message
                                            }
                                            onChange={option =>
                                                field.onChange(option?.value)
                                            }
                                            value={
                                                states.find(
                                                    option =>
                                                        option.label ===
                                                            field.value ||
                                                        option.value ===
                                                            field.value,
                                                )?.value
                                            }
                                            isDisabled={!states.length}
                                        />
                                    )}
                                />
                            </div>
                            <div className="col-6">
                                <Input
                                    inputStyle={InputStyles.outline}
                                    label="City"
                                    placeholder="Enter City..."
                                    error={errors.address?.city?.message}
                                    {...register('address.city')}
                                />
                            </div>
                            <div className="col-6">
                                <Input
                                    inputStyle={InputStyles.outline}
                                    label="Postal Code"
                                    placeholder="Enter Postal Code..."
                                    error={errors.address?.postalCode?.message}
                                    {...register('address.postalCode')}
                                />
                            </div>
                            <div className="col-12">
                                <Input
                                    inputStyle={InputStyles.outline}
                                    label="Street"
                                    placeholder="Enter Street..."
                                    error={errors.address?.street?.message}
                                    {...register('address.street')}
                                />
                            </div>
                            <div className="col-12">
                                <TextArea
                                    inputStyle={InputStyles.outline}
                                    label="Note to service"
                                    placeholder="e.g. Call me once you arrive"
                                    error={errors.note?.message}
                                    {...register('note')}
                                />
                            </div>
                            <div className="col-12">
                                <div className="row gutters-16">
                                    <div className="col-12">
                                        <div className="form-group-label">
                                            <label className="label">
                                                Appointment date & time
                                            </label>
                                        </div>
                                    </div>
                                    <div className="col-6">
                                        <Controller
                                            control={control}
                                            name="date"
                                            render={({ field }) => (
                                                <AppDatePicker
                                                    onChange={date =>
                                                        field.onChange(date)
                                                    }
                                                    selected={field.value}
                                                    dateFormat={'dd.MM.yyyy'}
                                                    placeholderText="Select start date"
                                                    error={errors.date?.message}
                                                    minDate={new Date()}
                                                />
                                            )}
                                        />
                                    </div>
                                    <div className="col-6">
                                        <Controller
                                            control={control}
                                            name="timeRange"
                                            render={({ field }) => (
                                                <TimeRangePicker
                                                    onChange={date =>
                                                        field.onChange(date)
                                                    }
                                                    value={field.value}
                                                    inputStyle={
                                                        InputStyles.outline
                                                    }
                                                    error={
                                                        errors.timeRange
                                                            ?.message
                                                    }
                                                />
                                            )}
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className="col-12">
                                <Controller
                                    control={control}
                                    name="assignments.refId"
                                    render={({ field }) => (
                                        <SelectBoxAsynk
                                            wrapperClassName="input--lg select-secondary select-left-spase-0"
                                            label="Assign technician team "
                                            placeholder="Select team"
                                            fetchFunk={loadTeams}
                                            value={selectedTeamOption}
                                            onChange={o => {
                                                setSelectedTeamOption(o)
                                                field.onChange(o?.value)
                                            }}
                                            error={
                                                errors.assignments?.refId
                                                    ?.message
                                            }
                                        />
                                    )}
                                />
                            </div>
                            <div className="col-12">
                                <Controller
                                    control={control}
                                    name="status"
                                    render={({ field }) => (
                                        <SelectBox
                                            wrapperClassName="input--lg select-secondary select-left-spase-0"
                                            label="Status"
                                            options={statusOptions}
                                            placeholder="Select status"
                                            error={errors.status?.message}
                                            onChange={option =>
                                                field.onChange(option?.value)
                                            }
                                        />
                                    )}
                                />
                            </div>
                        </div>
                    </div>
                    <div className="modal__footer justify-end">
                        <Button
                            loading={isSubmitting}
                            type="submit"
                            className="btn--primary"
                        >
                            Place an Order
                        </Button>
                    </div>
                </form>
            </div>
        </Modal>
    )
}

export default AddOrderModal
